import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as React from "react";
import {
  Stack,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Typography,
  Modal,
  Paper,
  Avatar,
  InputLabel,
  Divider,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Sidebar from "../Dashboard/Sidebar.js";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";

// Icons
import CallIcon from "@mui/icons-material/Call";
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";


// --- MODERN STYLED COMPONENTS ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const StyledDataGrid = styled(DataGrid)({
  border: "none",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    color: "#475569",
    fontWeight: 700,
    fontSize: "14px",
    borderBottom: "1px solid #E2E8F0",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #F1F5F9",
    color: "#1E293B",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #E2E8F0",
  },
  "& audio": {
    width: "100%",
    height: "32px",
    borderRadius: "8px",
  },
  "& audio::-webkit-media-controls-panel": {
    backgroundColor: "#F1F5F9",
    borderRadius: "8px",
  },
});

const StatChip = styled(Box)(({ color }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 18px",
  borderRadius: "12px",
  backgroundColor: `${color}15`,
  color: color,
  fontWeight: 600,
  fontSize: "0.9rem",
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "500px",
  maxHeight: "90vh",
  overflowY: "auto",
  backgroundColor: "#FFFFFF",
  borderRadius: "20px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
  outline: "none",
  [theme.breakpoints.down("sm")]: {
    width: "95%",
  },
}));

const CallLogs = () => {
  const [callLogs, setcallLogs] = React.useState([]);
  const [isLoading, setisLoading] = React.useState(true);
  const [TotalCallRecieved, setTotalCallRecieved] = React.useState(0);
  const [TotalCallMissed, setTotalCallMissed] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [salesExecutiveName, setSalesExecutiveName] = React.useState("");
  const [salesExecutiveCallReport, setSalesExecutiveCallsReport] =
    React.useState("");
  const [salesExecutiveCallMissedReport, setSalesExecutiveCallsMissedReport] =
    React.useState("");



  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSalesExecutiveName("");
    setSalesExecutiveCallsReport("");
    setSalesExecutiveCallsMissedReport("");
  };


  const baseColumns = [
    // {
    //   field: "UUID",
    //   headerName: "Call ID",
    //   width: 120,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <Typography sx={{ fontFamily: "monospace", fontSize: "0.85rem", color: "#64748B" }}>
    //       #{params.value?.substring(0, 8)}
    //     </Typography>
    //   ),
    // },
    {
      field: "callStatus",
      headerName: "Status",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          icon={
            params.value === "ANSWERED" ? (
              <CallReceivedIcon sx={{ fontSize: 16 }} />
            ) : (
              <PhoneMissedIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            fontWeight: 800,
            fontSize: "0.75rem",
            backgroundColor:
              params.value === "ANSWERED" ? "#DCFCE7" : "#FEE2E2",
            color: params.value === "ANSWERED" ? "#166534" : "#991B1B",
            border: `1.5px solid ${params.value === "ANSWERED" ? "#10B981" : "#EF4444"}`,
            "& .MuiChip-icon": {
              color: params.value === "ANSWERED" ? "#166534" : "#991B1B",
            },
          }}
        />
      ),
    },
    {
      field: "callerId",
      headerName: "Caller ID",
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: "0.9rem" }}>{params.value}</Typography>
        </Stack>
      ),
    },
    {
      field: "operatorName",
      headerName: "Sales Executive",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.9rem" }}>{params.value}</Typography>
      ),
    },
    {
      field: "ivrsCallPath",
      headerName: "Call Recording",
      width: 350,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            "& audio": {
              width: "100%",
              height: "36px",
              borderRadius: "10px",
              outline: "none",
            },
          }}
        >
          {params.value ? (
            <audio controls>
              <source src={params.value} type="audio/mp3" />
              Your browser does not support the audio element.
            </audio>
          ) : (
            <Chip
              label="No Recording"
              size="small"
              sx={{ bgcolor: "#F1F5F9", color: "#64748B", fontWeight: 600 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "callDuration",
      headerName: "Duration",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AccessTimeIcon sx={{ fontSize: 16, color: "#64748B" }} />
          <Typography
            sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#475569" }}
          >
            {params.value || "N/A"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "dateTime",
      headerName: "Timestamp",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.875rem", color: "#64748B" }}>
          {params.value}
        </Typography>
      ),
    },
  ];



  const fetchData = async () => {
    setisLoading(true);
    const roleID = localStorage.getItem("roleId");
    const token = localStorage.getItem("accessToken");
    const salesExecutive = localStorage.getItem("fullName");
    const config = { headers: { auth: token } };

    try {
      const endpoint =
        roleID !== "af7c1fe6-d669-414e-b066-e9733f0de7a8"
          ? `/getCallLogs/${salesExecutive}`
          : "/getCallLogs";

      const response = await axios.get(endpoint, config);
      setcallLogs(response.data.data);

      const countEndpoint =
        roleID !== "af7c1fe6-d669-414e-b066-e9733f0de7a8"
          ? `/getCallsCountTodaySalesExecutive/${salesExecutive}`
          : "/getCallsCountToday";

      const countRes = await axios.get(countEndpoint, config);
      setTotalCallRecieved(countRes.data.count);

      const missedEndpoint =
        roleID !== "af7c1fe6-d669-414e-b066-e9733f0de7a8"
          ? `/getCallsCountMissedTodaySalesExecutive/${salesExecutive}`
          : "/getCallsCountMissedToday";

      const missedRes = await axios.get(missedEndpoint, config);
      setTotalCallMissed(missedRes.data.count);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load call logs");
    }
    setisLoading(false);
  };

  const getSalesExecutiveReportFunc = async () => {
    if (!salesExecutiveName) {
      toast.warn("Please select a sales executive");
      return;
    }

    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };
    try {
      const [calls, missed] = await Promise.all([
        axios.get(
          `/getCallsCountTodaySalesExecutive/${salesExecutiveName}`,
          config,
        ),
        axios.get(
          `/getCallsCountMissedTodaySalesExecutive/${salesExecutiveName}`,
          config,
        ),
      ]);
      setSalesExecutiveCallsReport(calls.data.count);
      setSalesExecutiveCallsMissedReport(missed.data.count);
      toast.success("Report generated successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate report");
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const totalCalls = TotalCallRecieved + TotalCallMissed;
  const answerRate =
    totalCalls > 0 ? ((TotalCallRecieved / totalCalls) * 100).toFixed(1) : 0;

  return (
    <RootContainer>
      <Sidebar />
      <ContentArea>
        {/* HEADER SECTION */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#1E293B",
                fontFamily: "Kamerik",
                letterSpacing: "-1.5px",
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              Call Analytics
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748B", fontWeight: 500 }}
            >
              Complete IVR call history with recordings and performance metrics
            </Typography>
          </Box>

          {localStorage.getItem("roleId") ===
            "af7c1fe6-d669-414e-b066-e9733f0de7a8" && (
            <Button
              variant="contained"
              startIcon={<AssessmentIcon />}
              onClick={handleOpen}
              sx={{
                borderRadius: "12px",
                px: 3,
                bgcolor: "#1E293B",
                "&:hover": { bgcolor: "#0F172A" },
                width: { xs: "100%", md: "auto" },
                height: { xs: "48px", md: "auto" },
              }}
            >
              Advance Report
            </Button>
          )}
        </Stack>

        {/* STATS SECTION */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 4, flexWrap: "wrap" }}
        >
          <StatChip color="#10B981">
            <CallReceivedIcon fontSize="small" />
            Calls Received Today: {TotalCallRecieved}
          </StatChip>

          <StatChip color="#EF4444">
            <PhoneMissedIcon fontSize="small" />
            Calls Missed Today: {TotalCallMissed}
          </StatChip>

          <StatChip color="#3B82F6">
            <CallIcon fontSize="small" />
            Answer Rate: {answerRate}%
          </StatChip>
        </Stack>

        {/* DATA GRID SECTION */}
        <GlassCard elevation={0}>
          <Box sx={{ height: "65vh", width: "100%" }}>
            <StyledDataGrid
              rows={callLogs}
              columns={baseColumns}
              getRowId={(row) => row.UUID}
              loading={isLoading}
              disableSelectionOnClick
              checkboxSelection
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              pageSize={20}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{
                "& .MuiDataGrid-toolbarContainer": {
                  padding: "16px",
                  gap: "12px",
                  borderBottom: "1px solid #E2E8F0",
                  "& .MuiButton-root": {
                    color: "#64748B",
                    fontWeight: 700,
                    "&:hover": {
                      color: "#1E293B",
                    },
                  },
                },
              }}
            />
          </Box>

          {/* Summary Footer */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 3, sm: 0 },
              pt: 3,
              mt: 2,
              borderTop: "1px solid #E2E8F0",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#64748B", fontWeight: 600 }}
            >
              Showing {callLogs.length} call records
            </Typography>
            <Stack
              direction="row"
              spacing={3}
              sx={{ alignSelf: { xs: "center", sm: "auto" } }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748B", display: "block" }}
                >
                  Total Calls
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "#3B82F6" }}
                >
                  {totalCalls}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748B", display: "block" }}
                >
                  Answered
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "#10B981" }}
                >
                  {TotalCallRecieved}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748B", display: "block" }}
                >
                  Missed
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "#EF4444" }}
                >
                  {TotalCallMissed}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </GlassCard>
      </ContentArea>


      {/* ADVANCE REPORT MODAL */}
      <Modal open={open} onClose={handleClose}>
        <ModalBox>
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
              color: "#fff",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AssessmentIcon /> IVR Performance Analytics
            </Typography>
          </Box>

          <Box sx={{ p: 4 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#64748B", fontWeight: 600, mb: 2 }}
            >
              Select Sales Executive
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Executive Name</InputLabel>
              <Select
                value={salesExecutiveName}
                label="Executive Name"
                onChange={(e) => setSalesExecutiveName(e.target.value)}
                sx={{ borderRadius: "12px" }}
              >
                <MenuItem value="Riyaz Ahmed">Riyaz Ahmed</MenuItem>
                <MenuItem value="Adil Kamal">Adil Kamal</MenuItem>
                <MenuItem value="Biswadeep Roy">Biswadeep Roy</MenuItem>
                <MenuItem value="Syed Kamran Hassan">
                  Syed Kamran Hassan
                </MenuItem>
                <MenuItem value="Saad Ahmed">Saad Ahmed</MenuItem>
                <MenuItem value="Saif Ahmed">Saif Ahmed</MenuItem>
                <MenuItem value="Subham Das">Subham Das</MenuItem>
                <MenuItem value="Jaspreetraj Singh">Jaspreetraj Singh</MenuItem>
              </Select>
            </FormControl>

            <Button
              fullWidth
              variant="contained"
              onClick={getSalesExecutiveReportFunc}
              sx={{
                borderRadius: "12px",
                py: 1.8,
                mb: 3,
                bgcolor: "#eb1d27",
                fontWeight: 700,
                fontSize: "1rem",
                "&:hover": { bgcolor: "#9e1c29" },
              }}
            >
              Generate Report
            </Button>

            {salesExecutiveCallReport && (
              <Box
                sx={{
                  p: 3,
                  bgcolor: "#F8FAFC",
                  borderRadius: "16px",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#eb1d27",
                    fontWeight: 800,
                    mb: 2,
                    fontSize: "1rem",
                  }}
                >
                  📊 {salesExecutiveName}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#10B981",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "#64748B", fontWeight: 600 }}
                      >
                        Calls Received:
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: "#10B981" }}
                    >
                      {salesExecutiveCallReport}
                    </Typography>
                  </Stack>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#EF4444",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "#64748B", fontWeight: 600 }}
                      >
                        Calls Missed:
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: "#EF4444" }}
                    >
                      {salesExecutiveCallMissedReport}
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#1E293B", fontWeight: 700 }}
                    >
                      Answer Rate:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: "#3B82F6" }}
                    >
                      {salesExecutiveCallReport &&
                      salesExecutiveCallMissedReport
                        ? (
                            (salesExecutiveCallReport /
                              (salesExecutiveCallReport +
                                salesExecutiveCallMissedReport)) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Box>
        </ModalBox>
      </Modal>

      <ToastContainer position="bottom-right" />
    </RootContainer>
  );
};

export default CallLogs;
